import { auth } from '@modules/firebase/auth';
import { logError } from '@modules/logging';
import { Box, Button } from '@mui/material';
import { Component, ReactNode, type ErrorInfo } from 'react';
import { clearSelectedInterview } from '@modules/selected-interview';
import * as Sentry from '@sentry/react';

interface Props {
    children?: ReactNode | ReactNode[] | undefined;
    location: string;
}

interface State {
    hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): State {
        return { hasError: true };
    }

    componentDidUpdate(prevProps: Props) {
        const { location } = this.props;
        if (location !== prevProps.location) this.setState({ hasError: false });
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.log('componentDidCatch in ErrorBoundary');
        console.warn('Error in ErrorBoundary', { components: errorInfo.componentStack });

        if (error) {
            // Capture additional context for Sentry with React Router information
            Sentry.withScope((scope) => {
                scope.setTag('errorBoundary', this.props.location);
                scope.setTag('route', this.props.location);
                scope.setContext('errorInfo', {
                    componentStack: errorInfo.componentStack,
                    errorBoundary: this.props.location,
                });
                scope.setContext('routing', {
                    currentRoute: window.location.pathname,
                    search: window.location.search,
                    hash: window.location.hash,
                });
                scope.setLevel('error');
                logError(error);
            });
        }
        // logError(new Error(`${error.message} ${this.props.location} ${errorInfo.componentStack}`));
    }

    render() {
        if (this.state.hasError) {
            // You can render any custom fallback UI
            return (
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 10,
                    }}
                >
                    <h1>Something went wrong.</h1>
                    <h3>We've logged the error and will look into it. Please come back later.</h3>
                    <div style={{ marginTop: '2rem' }}>
                        {history.length > 1 ? (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => history.back()}
                                style={{ marginRight: '1rem' }}
                                size="large"
                            >
                                Go Back
                            </Button>
                        ) : null}
                        <Button
                            variant="outlined"
                            color="secondary"
                            style={{ marginRight: '1rem' }}
                            onClick={() => window.location.reload()}
                            size="large"
                        >
                            Reload Page
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => {
                                clearSelectedInterview();
                                auth().signOut();
                            }}
                            size="large"
                        >
                            Log Out
                        </Button>
                    </div>
                </Box>
            );
        }
        return this.props.children;
    }
}

export default ErrorBoundary;
